import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Country, City } from "country-state-city";
import toast from "react-hot-toast";

import ClubbieModal from "../Modal/ClubbieModal";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import DatePicker from "../DatePicker/DatePicker";
import Input from "../../shared/components/Input";
import UserIcon from "../../assets/images/user.png";
import uploadIcon from "../../assets/images/plus.png";
import {
  editProfile,
  fetchStockAvatars,
  uploadProfileImage,
} from "../../redux/actions/authActions";


/**
 * EditProfileModal — Multi-step edit profile wizard.
 */
const EditProfileModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const profile = user?.user?.profile || {};

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Personal details
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("Male");
  const [dateOfBirth, setDateOfBirth] = useState("");

  // Image state
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [isStock, setIsStock] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [stockAvatars, setStockAvatars] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);

  // Location state
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  const countryDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const countrySearchRef = useRef(null);
  const citySearchRef = useRef(null);

  // Load countries
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  // Load cities when country changes
  useEffect(() => {
    if (selectedCountry) {
      setCities(City.getCitiesOfCountry(selectedCountry) || []);
    } else {
      setCities([]);
    }
  }, [selectedCountry]);

  // Prefill when modal opens
  useEffect(() => {
    if (!isOpen || !profile) return;
    setSubmitError("");

    setFirstName(profile.firstName || "");
    setLastName(profile.secondName || "");
    setProfileImageUrl(profile.image || "");
    setSelectedFile(null);
    setPreview(null);
    setSelectedAvatar(null);
    setIsStock(false);

    if (profile.gender) {
      const g = profile.gender.toLowerCase();
      setGender(g === "male" ? "Male" : g === "female" ? "Female" : "Other");
    }

    if (profile.dateOfBirth) {
      const d = new Date(profile.dateOfBirth);
      if (!Number.isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        setDateOfBirth(`${yyyy}-${mm}-${dd}`);
      }
    }

    if (profile.country) {
      const allCountries = Country.getAllCountries();
      const match = allCountries.find(
        (c) => c.isoCode === profile.country || c.name.toLowerCase() === profile.country.toLowerCase()
      );
      if (match) setSelectedCountry(match.isoCode);
    }

    if (profile.city) {
      setSelectedCity(profile.city);
    }
  }, [isOpen, profile]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
        setIsCountryDropdownOpen(false);
        setCountrySearch("");
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
        setIsCityDropdownOpen(false);
        setCitySearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-focus search inputs when dropdowns open
  useEffect(() => {
    if (isCountryDropdownOpen && countrySearchRef.current) countrySearchRef.current.focus();
  }, [isCountryDropdownOpen]);
  useEffect(() => {
    if (isCityDropdownOpen && citySearchRef.current) citySearchRef.current.focus();
  }, [isCityDropdownOpen]);

  // Avatar helpers
  const loadStockAvatars = useCallback(async () => {
    if (stockAvatars.length > 0) return;
    try {
      setLoadingAvatars(true);
      const avatars = await fetchStockAvatars();
      setStockAvatars(avatars);
    } catch {
      // silently fail
    } finally {
      setLoadingAvatars(false);
    }
  }, [stockAvatars.length]);

  const openAvatarModal = () => {
    setIsAvatarModalOpen(true);
    loadStockAvatars();
  };

  const handleSaveAvatar = () => {
    if (selectedAvatar) {
      setProfileImageUrl(selectedAvatar.uri);
      setIsStock(true);
    } else if (selectedFile && preview) {
      setProfileImageUrl(preview);
      setIsStock(false);
    }
    setIsAvatarModalOpen(false);
  };

  const handleCancelAvatar = () => {
    setSelectedAvatar(null);
    setSelectedFile(null);
    setPreview(null);
    setValidationError(null);
    setIsAvatarModalOpen(false);
  };

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
    setSelectedFile(null);
    setPreview(null);
    setValidationError(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setValidationError(null);
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/svg+xml", "image/gif"];
    if (!validTypes.includes(file.type)) {
      setValidationError("Invalid file type. Please upload a SVG, PNG, JPG, or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setValidationError("File size too large. Maximum size is 5MB.");
      return;
    }
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      setSelectedFile(file);
      setPreview(img.src);
      setSelectedAvatar(null);
    };
    img.onerror = () => setValidationError("Invalid image file.");
  };

  // Location helpers
  const handleCountrySelect = (country) => {
    setSelectedCountry(country.isoCode);
    setSelectedCity("");
    setIsCountryDropdownOpen(false);
    setCountrySearch("");
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city.name);
    setIsCityDropdownOpen(false);
    setCitySearch("");
  };

  const getSelectedCountryName = () => {
    const country = countries.find((c) => c.isoCode === selectedCountry);
    return country ? country.name : "";
  };

  // Save
  const handleSave = async () => {
    setSubmitError("");

    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError("Please provide both first and last name.");
      return;
    }
    if (!dateOfBirth) {
      setSubmitError("Please select your date of birth.");
      return;
    }

    setIsSubmitting(true);

    try {
      let imageUrl = profileImageUrl;
      if (selectedFile && !isStock) {
        imageUrl = await uploadProfileImage(selectedFile);
      } else if (isStock && selectedAvatar) {
        imageUrl = selectedAvatar.uri;
      }

      const selectedCountryData = countries.find((c) => c.isoCode === selectedCountry);

      const updateBody = {
        fullName: `${firstName.trim()} ${lastName.trim()}`,
        firstName: firstName.trim(),
        secondName: lastName.trim(),
        gender: gender === "Other" ? "unknown" : gender ? gender.toLowerCase() : "",
        dateOfBirth,
        ...(imageUrl && { image: imageUrl }),
        ...(selectedCountryData && { country: selectedCountryData.name }),
        ...(selectedCity && { city: selectedCity }),
      };

      dispatch(
        editProfile(
          updateBody,
          () => {
            setIsSubmitting(false);
            toast.success("Profile updated successfully");
            onClose();
          },
          () => {
            setIsSubmitting(false);
            setSubmitError("Failed to update profile. Please try again.");
          }
        )
      );
    } catch {
      setIsSubmitting(false);
      setSubmitError("Failed to upload image. Please try again.");
    }
  };

  const renderAvatarImage = () => {
    if (!profileImageUrl) return <img src={UserIcon} alt="User" className="w-8 h-8" />;
    return <img src={profileImageUrl} alt="Profile" className="w-full h-full rounded-full object-cover" />;
  };

  // Footer
  const footer = (
    <div className="flex justify-end gap-3 w-full">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        type="button"
        className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        onClick={handleSave}
        disabled={isSubmitting}
      >
        {isSubmitting ? <><LoadingSpinner size="small" /><span>Saving...</span></> : "Save"}
      </button>
    </div>
  );

  return (
    <>
      <ClubbieModal
        isOpen={isOpen}
        width="600px"
        maxHeight="85vh"
        toggle={onClose}
        title="Edit profile"
        footer={footer}
      >
        <div className="flex flex-col gap-4 overflow-y-auto">
          {/* Image */}
          <div>
            <label className="block text-[14px] font-normal text-app-text mb-1.5">Image</label>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-[80px] h-[80px] border border-dashed border-[#3a4453] rounded-full flex items-center justify-center overflow-hidden bg-transparent">
                  {renderAvatarImage()}
                </div>
                <button
                  type="button"
                  onClick={openAvatarModal}
                  className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-[#06203A] flex items-center justify-center hover:scale-110 transition-transform"
                >
                  <img src={uploadIcon} alt="Upload" className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[14px] font-normal text-app-text mb-1.5">First Name</label>
              <Input type="text" placeholder="Enter first name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <label className="block text-[14px] font-normal text-app-text mb-1.5">Last Name</label>
              <Input type="text" placeholder="Enter last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-[14px] font-normal text-app-text mb-1.5">Gender</label>
            <div className="flex flex-col gap-2">
              {["Male", "Female", "Other"].map((option) => (
                <label key={option} className="relative inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="gender-edit" value={option} checked={gender === option} onChange={(e) => setGender(e.target.value)} className="sr-only peer" />
                  <div className="w-4 h-4 rounded-full border-[1.5px] border-[#333E48] peer-checked:border-blue-shade-2 flex items-center justify-center transition-colors">
                    {gender === option && <div className="w-2 h-2 rounded-full bg-blue-shade-2" />}
                  </div>
                  <span className="text-sm text-[#FEFCEB]">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-[14px] font-normal text-app-text mb-1.5">Date of birth</label>
            <DatePicker value={dateOfBirth} onChange={(val) => setDateOfBirth(val)} disablePastDates={false} />
          </div>

          {/* Country */}
          <div>
            <label className="block text-[14px] font-normal text-app-text mb-1.5">Country</label>
            <div className="relative" ref={countryDropdownRef}>
              <div
                className={`flex items-center justify-between h-11 px-4 bg-[#021529] border rounded-full cursor-pointer transition-colors ${isCountryDropdownOpen ? "border-[#00E7FF]" : "border-transparent"}`}
                onClick={() => { setIsCountryDropdownOpen(!isCountryDropdownOpen); setIsCityDropdownOpen(false); }}
              >
                <span className={`text-[15px] ${selectedCountry ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
                  {getSelectedCountryName() || "Select a country"}
                </span>
                <svg className={`w-4 h-4 text-[#FEFCEB] transition-transform ${isCountryDropdownOpen ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              {isCountryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-app-bg border border-[#192634] rounded-[20px] shadow-lg overflow-hidden z-[1000]">
                  <div className="flex items-center gap-2 px-3 border-b border-[#192634]">
                    <svg className="w-4 h-4 text-[#7F8585] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                      ref={countrySearchRef}
                      type="text"
                      className="flex-1 h-10 bg-transparent text-[#FEFCEB] text-sm outline-none placeholder-[#7F8585]"
                      placeholder="Search"
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="max-h-[250px] overflow-y-auto">
                    {countries.filter((c) => c.name.toLowerCase().includes(countrySearch.toLowerCase())).length > 0 ? (
                      countries.filter((c) => c.name.toLowerCase().includes(countrySearch.toLowerCase())).map((country) => (
                        <div
                          key={country.isoCode}
                          className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors border-b border-[#192634] last:border-b-0 hover:bg-app-hover ${
                            selectedCountry === country.isoCode ? "bg-app-hover" : ""
                          }`}
                          onClick={() => handleCountrySelect(country)}
                        >
                          <span className="text-lg">{country.flag}</span>
                          <span className={`text-sm font-medium ${selectedCountry === country.isoCode ? "text-[#00E7FF]" : "text-[#FEFCEB]"}`}>{country.name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-[#7F8585] text-sm">No countries found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* City */}
          <div>
            <label className="block text-[14px] font-normal text-app-text mb-1.5">City</label>
            <div className="relative" ref={cityDropdownRef}>
              <div
                className={`flex items-center justify-between h-11 px-4 bg-[#021529] border rounded-full transition-colors ${
                  !selectedCountry ? "opacity-50 cursor-not-allowed border-transparent" : isCityDropdownOpen ? "border-[#00E7FF] cursor-pointer" : "border-transparent cursor-pointer"
                }`}
                onClick={() => { if (selectedCountry) { setIsCityDropdownOpen(!isCityDropdownOpen); setIsCountryDropdownOpen(false); } }}
              >
                <span className={`text-[15px] ${selectedCity ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
                  {selectedCity || (selectedCountry ? "Select a city" : "Please select a country first")}
                </span>
                <svg className={`w-4 h-4 text-[#FEFCEB] transition-transform ${isCityDropdownOpen ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              {isCityDropdownOpen && selectedCountry && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-app-bg border border-[#192634] rounded-[20px] shadow-lg overflow-hidden z-[1000]">
                  <div className="flex items-center gap-2 px-3 border-b border-[#192634]">
                    <svg className="w-4 h-4 text-[#7F8585] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                      ref={citySearchRef}
                      type="text"
                      className="flex-1 h-10 bg-transparent text-[#FEFCEB] text-sm outline-none placeholder-[#7F8585]"
                      placeholder="Search"
                      value={citySearch}
                      onChange={(e) => setCitySearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="max-h-[250px] overflow-y-auto">
                    {cities.filter((c) => c.name.toLowerCase().includes(citySearch.toLowerCase())).length > 0 ? (
                      cities.filter((c) => c.name.toLowerCase().includes(citySearch.toLowerCase())).map((city) => (
                        <div
                          key={city.name}
                          className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors border-b border-[#192634] last:border-b-0 hover:bg-app-hover ${
                            selectedCity === city.name ? "bg-app-hover" : ""
                          }`}
                          onClick={() => handleCitySelect(city)}
                        >
                          <span className={`text-sm font-medium ${selectedCity === city.name ? "text-[#00E7FF]" : "text-[#FEFCEB]"}`}>{city.name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-[#7F8585] text-sm">No cities found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Error */}
          {submitError && <p className="text-sm text-red-500">{submitError}</p>}
        </div>
      </ClubbieModal>

      {/* Avatar Sub-Modal */}
      <ClubbieModal
        isOpen={isAvatarModalOpen}
        toggle={() => setIsAvatarModalOpen(false)}
        title="Add Profile Picture"
        minHeight="auto"
        maxHeight="85vh"
        footer={
          <>
            <button type="button" onClick={handleCancelAvatar} className="px-6 py-2.5 bg-app-card rounded-full text-app-text hover:bg-[#1e3a57] transition-colors text-sm font-medium">
              Cancel
            </button>
            <button type="button" onClick={handleSaveAvatar} disabled={!selectedAvatar && !selectedFile} className="px-6 py-2.5 rounded-full bg-[#00E7FF] text-[#021529] text-sm font-medium transition-colors hover:bg-[#00d4eb] disabled:opacity-40 disabled:cursor-not-allowed">
              Confirm
            </button>
          </>
        }
      >
        <div className="text-[#FEFCEB] px-3 py-3 max-w-md mx-auto">
          <div className="flex justify-center mb-5">
            <div className="w-28 h-28 rounded-full border border-blue-shade-7 flex items-center justify-center overflow-hidden bg-blue-shade-9">
              {selectedAvatar ? (
                <img src={selectedAvatar.uri} alt="Selected avatar" className="w-full h-full object-cover" />
              ) : preview ? (
                <img src={preview} alt="Uploaded preview" className="w-full h-full object-cover" />
              ) : (
                <img src={UserIcon} alt="User" className="w-10 h-10 opacity-30" />
              )}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="editProfileModalFileInput" className="flex items-center justify-center gap-2 w-full h-10 rounded-full bg-[#082441] text-[#FEFCEB] text-sm font-proxima cursor-pointer hover:bg-[#0a2f57] transition-colors">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10m9.333-5.333L8 1.333m0 0L4.667 4.667M8 1.333v9.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Upload new photo
            </label>
            <input id="editProfileModalFileInput" type="file" accept=".svg,.png,.jpg,.jpeg,.gif" onChange={handleFileUpload} className="hidden" />
            {validationError && <p className="mt-2 text-xs text-red-500 text-center">{validationError}</p>}
          </div>

          <div className="flex items-center my-4 text-xs text-shaded-text">
            <span className="flex-1 h-px bg-blue-shade-8" />
            <span className="px-3">or</span>
            <span className="flex-1 h-px bg-blue-shade-8" />
          </div>

          <p className="text-xs text-shaded-text mb-3">Choose from the gallery</p>

          {loadingAvatars ? (
            <div className="text-center text-sm text-app-text-secondary py-4">Loading avatars...</div>
          ) : (
            <div className="grid grid-cols-3 gap-8 px-6">
              {stockAvatars.map((avatar) => (
                <button key={avatar.id} type="button" onClick={() => handleAvatarSelect(avatar)} className="relative w-full aspect-square transition-all hover:scale-105">
                  <div className="w-full h-full rounded-full overflow-hidden border-2 bg-blue-shade-9" style={{ borderColor: selectedAvatar?.id === avatar.id ? "#00E7FF" : "transparent" }}>
                    <img src={avatar.uri} alt="Avatar" className="w-full h-full object-cover" />
                  </div>
                  {selectedAvatar?.id === avatar.id && (
                    <div className="absolute bottom-1 right-1 w-5 h-5 bg-[#00E7FF] rounded-full flex items-center justify-center">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 3L4.5 8.5L2 6" stroke="#081725" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </ClubbieModal>
    </>
  );
};

export default EditProfileModal;
